import * as React from 'react'
import { ReactElement, useState } from 'react'
import { Formik } from 'formik'
import { ISelection } from '@fluentui/react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import useProcessEmail, { SelectableEmail } from '../../../../hooks/email/useProcessEmail'
import useUser from '../../../../hooks/useUser'
import { normalizeAxiosError, NormalizedError } from '../../../../reducers/errorReducer'
import { DataPromise } from '../../../../context/AxiosContext'
import { IFileDto } from '../../../../config/fileConfig'
import { FileDataAction } from '../../../../hooks/file/useFileData'
import { EmailProcessingAction } from '../../../config/emailProcessingConfig'
import FormContent from './FormContent'

export type ProcessEmailFormProps = {
    /** The e-mail address in which the e-mail resides. Can be the users or the replications. */
    emailMailbox: string
    action: EmailProcessingAction
    selection: ISelection<SelectableEmail>
    onDismiss: () => void
    /** The file the e-mails are listed in; a Move replaces its reference. Absent in the global inbox and the popup. */
    file?: DataPromise<IFileDto>
    isLoading?: boolean
    /** Refreshes the list the host shows once the e-mails have been processed. */
    reset?: FileDataAction
    /** Keeps a click inside the dialog from reaching the list behind it. Only the v8 toolbars need this. */
    setPreventDefaultEvent?: (value: boolean) => void
    /**
     * References the e-mail already carries. The suggestion popup passes them so that processing an e-mail a second
     * time adds a file instead of replacing the one it was filed in before (HAM-4278).
     */
    initialReferences?: string[]
    setSubmitForm?: (submit: () => Promise<any>) => void
    setIsSubmitting?: (isSubmitting: boolean) => void
    globalErrors?: NormalizedError[]
}

export type ProcessEmailValues = {
    references: string[]
    userEmail: string
    notificationEmail: string
}

// Construct new set of references for the selected email(s).
function handleReferences(action: EmailProcessingAction, oldReference: string, originalReferences: string[], newReferences: string[]) {
    if (action === EmailProcessingAction.Move) {
        return [...new Set(originalReferences.filter(ref => ref !== oldReference).concat(newReferences))]
    } else if (action === EmailProcessingAction.Copy) {
        return [...new Set(originalReferences.concat(newReferences))]
    } else if (action === EmailProcessingAction.File) {
        return [...new Set(newReferences)]
    } else if (action === EmailProcessingAction.Ignore) {
        return []
    } else {
        return originalReferences
    }
}

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        references: Yup.array().test({
            message: translate('email.filing.validation'),
            test: arr => arr.length > 0,
        }),
    })
}

export default function ProcessEmailForm(props: ProcessEmailFormProps): ReactElement {
    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const { processSelection } = useProcessEmail()
    const { currentEmail } = useUser()
    const { t: translate } = useTranslation()

    return (
        <Formik<ProcessEmailValues>
            initialValues={{
                references: props.initialReferences ?? [],
                userEmail: props.emailMailbox,
                notificationEmail: currentEmail,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={props.action !== EmailProcessingAction.Ignore && getValidationSchema(translate)}
            onSubmit={(values, formikHelpers) => {
                processSelection(
                    values.userEmail,
                    values.notificationEmail,
                    props.selection.getSelection(),
                    references => handleReferences(props.action, props.file?.data?.reference, references, values.references),
                    () => {
                        setGlobalErrors(undefined)
                        props.selection.setChangeEvents(false)
                    },
                    () => {
                        if (!globalErrors) {
                            props.onDismiss()
                            props.reset?.()
                            formikHelpers.resetForm()
                        }
                        props.selection.setChangeEvents(true)
                    },
                ).catch(error => setGlobalErrors([normalizeAxiosError(error)]))
            }}
        >
            {/* The errors from the submit above were collected but never shown before; the host's errors still win. */}
            <FormContent {...props} globalErrors={props.globalErrors ?? globalErrors} />
        </Formik>
    )
}
